import { useState, useRef, useEffect } from 'react';
import { Bell, CheckCheck, Megaphone, User } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';
import { useNotifications } from '../hooks/useNotifications';
import { usePushSubscription } from '@/lib/pwa/usePushSubscription';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { PushSubscriptionBanner } from './PushSubscriptionBanner';

export function NotificationDropdown() {
  const { user } = useAuth();
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const { permission, isSubscribed, subscribe, isLoading } = usePushSubscription();

  const pushState = isSubscribed
    ? 'subscribed'
    : (permission as 'unsupported' | 'denied' | 'default');
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const bellButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Escape to close
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        bellButtonRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  // Focus first interactive element when dialog opens
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const focusable = dropdownRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    focusable[0]?.focus();
  }, [isOpen]);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={bellButtonRef}
        onClick={() => setIsOpen((prev) => !prev)}
        className="text-ink-2 hover:bg-surface-soft rounded-control relative flex h-11 w-11 items-center justify-center transition-[color,background-color,transform] active:scale-[0.96]"
        aria-label="התראות"
        aria-expanded={isOpen}
        aria-haspopup="dialog"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span className="bg-danger absolute top-1 right-1 flex h-[18px] min-w-[18px] items-center justify-center rounded-full px-1 text-[11px] font-bold text-white tabular-nums">
            {unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          role="dialog"
          aria-label="התראות"
          className="rounded-card bg-surface text-ink fixed inset-x-4 top-14 z-50 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.06),0_8px_24px_rgba(0,0,0,0.12)] sm:absolute sm:inset-x-auto sm:top-auto sm:left-0 sm:mt-2 sm:w-96"
        >
          {/* Header */}
          <div className="border-line bg-surface-soft flex items-center justify-between border-b px-4 py-3">
            <h3 className="text-ink text-sm font-bold text-balance">הודעות</h3>
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsRead()}
                className="text-brand hover:text-brand-ink -m-2 flex min-h-10 items-center gap-1 p-2 text-xs font-semibold transition-[color,transform] active:scale-[0.96]"
              >
                <CheckCheck size={14} />
                סמן הכל כנקרא
              </button>
            )}
          </div>

          {/* Push notification prompt — unsupported / denied / default */}
          <PushSubscriptionBanner
            variant="compact"
            pushState={pushState}
            onSubscribe={subscribe}
            isLoading={isLoading}
          />

          {/* List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
                <div className="bg-surface-soft mb-3 flex h-12 w-12 items-center justify-center rounded-full">
                  <Bell size={22} className="text-sub" />
                </div>
                <p className="text-muted text-sm font-medium">אין הודעות חדשות</p>
                <p className="text-sub mt-1 text-xs">כשתקבל הודעות, הן יופיעו כאן</p>
              </div>
            ) : unreadCount === 0 ? (
              <div className="flex flex-col items-center justify-center px-4 py-10 text-center">
                <div className="bg-success-soft mb-3 flex h-12 w-12 items-center justify-center rounded-full">
                  <CheckCheck size={22} className="text-success" />
                </div>
                <p className="text-muted text-sm font-medium">אין הודעות שלא נקראו</p>
                <p className="text-sub mt-1 text-xs">כל ההודעות נקראו</p>
              </div>
            ) : (
              notifications
                .filter((n) => !n.readBy?.[user?.uid || ''])
                .map((notif) => {
                  const createdAt = notif.createdAt?.toDate?.();
                  return (
                    <div
                      key={notif.id}
                      onClick={() => markAsRead(notif.id)}
                      className="border-line bg-brand-soft/50 hover:bg-brand-soft flex cursor-pointer items-start gap-3 border-b px-4 py-3 transition-colors"
                    >
                      <div
                        className={`rounded-chip flex h-8 w-8 flex-shrink-0 items-center justify-center ${
                          notif.type === 'broadcast' ? 'bg-info-soft' : 'bg-success-soft'
                        }`}
                      >
                        {notif.type === 'broadcast' ? (
                          <Megaphone size={15} className="text-info" />
                        ) : (
                          <User size={15} className="text-success" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="text-ink truncate text-sm font-bold">{notif.title}</p>
                          <span className="bg-brand h-1.5 w-1.5 flex-shrink-0 rounded-full" />
                        </div>
                        <p className="text-muted mt-0.5 line-clamp-2 text-xs">{notif.body}</p>
                        {createdAt && (
                          <p
                            className="text-sub mt-1 text-[11px] tabular-nums"
                            title={format(createdAt, 'dd/MM HH:mm', { locale: he })}
                          >
                            {formatDistanceToNow(createdAt, { addSuffix: true, locale: he })}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
